import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ThinkingOrb } from 'thinking-orbs';

/**
 * ThinkingOrbLoader
 *
 * Lightweight wrapper around `ThinkingOrb` from 'thinking-orbs' tuned for Tezer.
 * Renders on 2D canvas with 0 GPU/WebGL overhead and automatic pause when off-screen or tab hidden.
 * Supports anchored `popup` mode to display a prominent 64px floating orb directly above/over
 * clicked action buttons while keeping button dimensions stable.
 *
 * @param {Object} props
 * @param {'working' | 'searching' | 'solving' | 'listening' | 'connecting' | 'weaving' | 'composing' | 'breathing' | 'shaping'} [props.state='working']
 * @param {20 | 32 | 64} [props.size=20] Inline size for buttons/badges
 * @param {boolean} [props.popup=false] Whether to pop up a large floating orb from the button
 * @param {20 | 32 | 64} [props.popupSize=64] Size of the orb inside the pop-up
 * @param {number} [props.popupScale=1.3] Visual scale factor of the orb inside the pop-up
 * @param {'auto' | 'top' | 'bottom'} [props.popupPosition='auto'] Preferred pop-up position
 * @param {string} [props.color='#00f5d4'] Tint hex/rgb color
 * @param {'auto' | 'dark' | 'light'} [props.theme='dark'] Tezer is dark-themed
 * @param {string} [props.text] Optional label to render beside the orb
 * @param {string} [props.label] Accessible aria-label
 * @param {React.CSSProperties} [props.style]
 * @param {React.CSSProperties} [props.containerStyle]
 */
function normalizeOrbSize(s) {
  const num = typeof s === 'number' ? s : parseInt(s, 10);
  if (isNaN(num) || num <= 24) return 20;
  if (num <= 48) return 32;
  return 64;
}

export function ThinkingOrbLoader({
  state = 'working',
  size = 20,
  popup = false,
  popupSize = 64,
  popupScale = 1.3,
  popupPosition = 'auto',
  color = '#00f5d4',
  theme = 'dark',
  text,
  label,
  style,
  containerStyle,
  speed = 1,
  ...rest
}) {
  const normalizedInlineSize = normalizeOrbSize(size);
  const normalizedPopupSize = normalizeOrbSize(popupSize);

  const anchorRef = useRef(null);
  const popupRef = useRef(null);
  const [coords, setCoords] = useState(null);
  const [popupDim, setPopupDim] = useState({ width: 240, height: 160 });

  // When popup mode is active, track the parent button's viewport bounding rect
  useEffect(() => {
    if (!popup) return;

    const el = anchorRef.current;
    if (!el) return;
    const triggerEl = el.closest('button, label, [role="button"]') || el.parentElement;
    if (!triggerEl) return;

    const updatePosition = () => {
      const rect = triggerEl.getBoundingClientRect();
      if (rect.width > 0 || rect.height > 0) {
        setCoords({
          top: rect.top,
          bottom: rect.bottom,
          left: rect.left,
          right: rect.right,
          width: rect.width,
          height: rect.height
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [popup]);

  // Dynamically update popup dimensions for perfect centering
  useLayoutEffect(() => {
    if (popup && popupRef.current) {
      const rect = popupRef.current.getBoundingClientRect();
      if (rect.width > 0 && (rect.width !== popupDim.width || rect.height !== popupDim.height)) {
        setPopupDim({ width: rect.width, height: rect.height });
      }
    }
  }, [popup, coords, text, popupSize, popupScale]);

  const inlineOrb = (
    <ThinkingOrb
      state={state}
      size={normalizedInlineSize}
      color={color}
      theme={theme}
      speed={speed}
      ariaLabel={label || text || (typeof state === 'string' ? `${state}...` : 'Loading...')}
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
        ...style
      }}
      {...rest}
    />
  );

  // If not popup mode, render standard inline loader
  if (!popup) {
    if (text) {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            verticalAlign: 'middle',
            ...containerStyle
          }}
        >
          {inlineOrb}
          <span>{text}</span>
        </span>
      );
    }
    return inlineOrb;
  }

  // Popup mode calculations
  const buttonCenterX = coords ? coords.left + coords.width / 2 : window.innerWidth / 2;
  const clampedLeft = Math.max(16, Math.min(window.innerWidth - popupDim.width - 16, buttonCenterX - popupDim.width / 2));
  const arrowLeft = Math.max(20, Math.min(popupDim.width - 20, buttonCenterX - clampedLeft));

  const preferTop = popupPosition === 'top' || (popupPosition === 'auto' && (!coords || coords.top >= popupDim.height + 24));
  const popupTop = coords
    ? (preferTop
        ? Math.max(16, coords.top - popupDim.height - 12)
        : Math.min(window.innerHeight - popupDim.height - 16, coords.bottom + 12))
    : window.innerHeight / 2 - popupDim.height / 2;

  const popupPortal = typeof document !== 'undefined' && coords ? createPortal(
    <div
      ref={popupRef}
      className="thinking-orb-popup"
      role="status"
      aria-live="polite"
      style={{
        top: `${popupTop}px`,
        left: `${clampedLeft}px`,
        borderColor: `${color}55`,
        boxShadow: `0 20px 60px rgba(0, 0, 0, 0.8), 0 0 36px ${color}35`,
      }}
    >
      <div
        style={{
          width: '84px',
          height: '84px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          margin: '2px 0'
        }}
      >
        <ThinkingOrb
          state={state}
          size={normalizedPopupSize}
          color={color}
          theme={theme}
          speed={speed}
          style={{ transform: `scale(${popupScale})`, transformOrigin: 'center center' }}
          ariaLabel={label || text || `${state}...`}
        />
      </div>
      {text && (
        <div
          style={{
            marginTop: '12px',
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--text-main, #f1f2f6)',
            textAlign: 'center',
            letterSpacing: '0.01em',
            textShadow: '0 2px 8px rgba(0,0,0,0.8)',
            maxWidth: '280px',
            lineHeight: 1.35
          }}
        >
          {text}
        </div>
      )}
      {/* Downward / Upward Pointer Arrow */}
      <div
        style={{
          position: 'absolute',
          [preferTop ? 'bottom' : 'top']: '-7px',
          left: `${arrowLeft}px`,
          transform: 'translateX(-50%) rotate(45deg)',
          width: '14px',
          height: '14px',
          background: 'rgba(10, 11, 16, 0.94)',
          borderRight: preferTop ? `1.5px solid ${color}55` : 'none',
          borderBottom: preferTop ? `1.5px solid ${color}55` : 'none',
          borderLeft: !preferTop ? `1.5px solid ${color}55` : 'none',
          borderTop: !preferTop ? `1.5px solid ${color}55` : 'none',
          zIndex: 1,
        }}
      />
    </div>,
    document.body
  ) : null;

  return (
    <>
      <span
        ref={anchorRef}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          verticalAlign: 'middle',
          opacity: 0.85,
          ...containerStyle
        }}
      >
        {inlineOrb}
        {text && <span>{text}</span>}
      </span>
      {popupPortal}
    </>
  );
}

export default ThinkingOrbLoader;
